import * as React from "react"
import { useLocation } from "@gatsbyjs/reach-router"

import { useMotionPreference } from "../utils/motion-preference"
import { requestHtmlInCanvasRefresh } from "../utils/html-in-canvas"

const PAGE_ORDER = ["/", "/music", "/activities", "/dicom", "/contact", "/memories"]

const normalizePath = (path) => {
  if (!path || path === "/") return "/"
  return path.replace(/\/+$/, "")
}

const pageIndex = (pathname) => {
  const p = normalizePath(pathname)
  const idx = PAGE_ORDER.findIndex(
    (route) => (route === "/" ? p === "/" : p === route || p.startsWith(`${route}/`))
  )
  return idx === -1 ? 0 : idx
}

const DURATION = 220

const PageTransition = ({ children }) => {
  const location = useLocation()
  const { reduceMotion } = useMotionPreference()
  const pathname = normalizePath(location.pathname)
  const prevPathRef = React.useRef(pathname)
  const childrenRef = React.useRef(children)
  const [displayChildren, setDisplayChildren] = React.useState(children)
  const [phase, setPhase] = React.useState("idle") // idle | exiting | entering
  const [direction, setDirection] = React.useState(1) // 1 = forward (left), -1 = backward (right)
  const timerRef = React.useRef(null)

  const refreshGlassClone = React.useCallback(() => {
    if (typeof window === "undefined") return
    window.requestAnimationFrame(() => requestHtmlInCanvasRefresh({ fullSync: true }))
  }, [])

  React.useEffect(() => {
    childrenRef.current = children
  }, [children])

  React.useEffect(() => {
    if (reduceMotion) {
      if (timerRef.current) window.clearTimeout(timerRef.current)
      prevPathRef.current = pathname
      setDisplayChildren(children)
      setPhase("idle")
      refreshGlassClone()
      return
    }

    if (pathname === prevPathRef.current) {
      // Same page, just update children directly
      setDisplayChildren(children)
      refreshGlassClone()
      return
    }

    const dir = pageIndex(pathname) >= pageIndex(prevPathRef.current) ? 1 : -1
    prevPathRef.current = pathname
    setDirection(dir)
    setPhase("exiting")

    // After exit animation, swap in new children and enter
    timerRef.current = window.setTimeout(() => {
      setDisplayChildren(childrenRef.current)
      setPhase("entering")

      timerRef.current = window.setTimeout(() => {
        setPhase("idle")
        refreshGlassClone()
      }, DURATION)
    }, DURATION)

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
  }, [pathname, reduceMotion, refreshGlassClone]) // eslint-disable-line react-hooks/exhaustive-deps

  const className = [
    "page-transition",
    phase === "exiting" ? "page-transition--exiting" : "",
    phase === "entering" ? "page-transition--entering" : "",
    direction === -1 ? "page-transition--reverse" : "",
  ]
    .filter(Boolean)
    .join(" ")

  return <div className={className}>{displayChildren}</div>
}

export default PageTransition
